import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import {
  Folder, FolderOpen, FolderPlus, Send, Plus, Upload, Trash2, Mail,
  ChevronLeft, LogOut, FileText, Settings, AlertTriangle, CheckCircle,
  XCircle, Clock, Inbox, RefreshCw, Sparkles, BarChart3, Pencil, Check, X,
  MessageSquare, Eye, Power, ShieldAlert, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ACCENT = '#e6007e'; // Paify pink — single brand identity

// ============== shared layout ==============
const ReachShell = ({ active, children }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');
  const [killState, setKillState] = useState(null); // { killed, updated_at, updated_by, default }
  const [toggling, setToggling] = useState(false);

  // Fetch kill switch state on mount + when active route changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/reach/kill-switch`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('admin_token')}` }
        });
        if (!cancelled) setKillState(res.data);
      } catch (err) {
        // Non-fatal: leave state null, UI will show "checking…"
      }
    })();
    return () => { cancelled = true; };
  }, [active]);

  const handleToggle = async () => {
    if (!killState) return;
    const newKilled = !killState.killed;
    // Strong confirmation when ENABLING sends (turning OFF the kill switch)
    if (!newKilled) {
      const confirmed = window.confirm(
        '⚠️ ENABLE OUTREACH SENDING?\n\n' +
        'Turning OFF the kill switch will allow Paify Reach to send emails ' +
        '(both scheduled and manual). Make sure your campaigns and SMTP are correct.\n\n' +
        'Continue?'
      );
      if (!confirmed) return;
    }
    setToggling(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/reach/kill-switch`,
        { killed: newKilled },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('admin_token')}` } }
      );
      setKillState(res.data);
      toast({
        title: newKilled ? '🛑 Outreach KILLED' : '✅ Outreach ENABLED',
        description: newKilled
          ? 'No outreach emails will be sent until you turn this back on.'
          : 'Outreach can now send emails. Switch back off any time.',
      });
    } catch (err) {
      toast({
        title: 'Could not change state',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setToggling(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };
  const Tab = ({ to, label, icon: Icon, testid }) => (
    <button
      onClick={() => navigate(to)}
      data-testid={testid}
      className={`px-4 py-3 whitespace-nowrap border-b-2 transition-colors ${
        active === to ? 'text-white' : 'text-gray-400 hover:text-white border-transparent'
      }`}
      style={active === to ? { borderColor: ACCENT } : {}}
    >
      <Icon className="w-4 h-4 inline mr-2" />{label}
    </button>
  );

  const isKilled = killState?.killed ?? true; // default to killed visually if unknown

  return (
    <div className="min-h-screen bg-gray-900" data-testid="reach-shell">
      {/* Header — original Paify pink identity */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Reach</span>
          </div>
          <div className="flex items-center gap-3">
            {/* MASTER KILL SWITCH — always visible */}
            <button
              type="button"
              onClick={handleToggle}
              disabled={toggling || killState === null}
              data-testid="outreach-kill-switch-btn"
              title={isKilled
                ? 'Outreach sending is BLOCKED. Click to enable.'
                : 'Outreach sending is ENABLED. Click to BLOCK all sends.'}
              className={`relative flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 transition-all text-xs font-semibold ${
                isKilled
                  ? 'bg-red-500/15 border-red-500 text-red-300 hover:bg-red-500/25'
                  : 'bg-emerald-500/15 border-emerald-500 text-emerald-300 hover:bg-emerald-500/25'
              } ${toggling || killState === null ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {toggling ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : isKilled ? (
                <ShieldAlert className="w-3.5 h-3.5" />
              ) : (
                <Power className="w-3.5 h-3.5" />
              )}
              <span data-testid="outreach-kill-switch-label">
                {killState === null ? 'Checking…' : isKilled ? 'Outreach OFF' : 'Outreach ON'}
              </span>
              {/* Toggle pill */}
              <span className={`ml-1 inline-block w-8 h-4 rounded-full relative transition-colors ${
                isKilled ? 'bg-red-500/50' : 'bg-emerald-500/70'
              }`}>
                <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                  isKilled ? 'left-0.5' : 'left-4'
                }`} />
              </span>
            </button>
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-xs text-gray-500 hover:text-gray-300 px-2 py-1 rounded border border-gray-700"
              data-testid="reach-back-to-paify"
            >
              ← Back to Paify Admin
            </button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Killed banner — UNMISSABLE red bar when outreach is paused */}
      {killState !== null && isKilled && (
        <div data-testid="outreach-killed-banner" className="bg-red-600/95 border-b border-red-500">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4 text-sm text-white">
            <div className="flex items-center gap-2 min-w-0">
              <ShieldAlert className="w-4 h-4 flex-shrink-0" />
              <span className="font-semibold">Master kill switch is ON — no outreach emails are being sent (scheduled or manual).</span>
            </div>
            <button
              onClick={handleToggle}
              disabled={toggling}
              className="text-xs underline hover:no-underline whitespace-nowrap flex-shrink-0"
              data-testid="outreach-banner-enable-btn"
            >
              {toggling ? 'Enabling…' : 'Enable sending →'}
            </button>
          </div>
        </div>
      )}

      {/* Nav: 7 tabs */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 flex gap-2 overflow-x-auto">
          <Tab to="/admin/reach" label="Overview" icon={BarChart3} testid="reach-nav-overview" />
          <Tab to="/admin/reach/folders" label="Folders" icon={Folder} testid="reach-nav-folders" />
          <Tab to="/admin/reach/templates" label="Templates" icon={FileText} testid="reach-nav-templates" />
          <Tab to="/admin/reach/smtp" label="SMTP" icon={Mail} testid="reach-nav-smtp" />
          <Tab to="/admin/reach/referrals" label="Referrals" icon={Send} testid="reach-nav-referrals" />
          <Tab to="/admin/reach/analytics" label="Analytics" icon={BarChart3} testid="reach-nav-analytics" />
          <Tab to="/admin/reach/settings" label="Settings" icon={Settings} testid="reach-nav-settings" />
        </div>
      </nav>
      <main className="max-w-7xl mx-auto px-4 py-8">{children}</main>
    </div>
  );
};

const tokenHeader = () => ({ Authorization: `Bearer ${sessionStorage.getItem('admin_token')}` });

// ============== Folders Home (also serves as Overview at /admin/reach) ==============
export const ReachFoldersPage = () => {
  const location = useLocation();
  const isOverview = location.pathname === '/admin/reach';
  const [folders, setFolders] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [discoSectors, setDiscoSectors] = useState([]);
  const [discoReady, setDiscoReady] = useState(false);
  const [discoForm, setDiscoForm] = useState({ sector: '', location: '', target_count: 50, folder: '' });
  const [discovering, setDiscovering] = useState(false);
  const [newName, setNewName] = useState('');
  const [dragOver, setDragOver] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [renaming, setRenaming] = useState(null); // folder name currently being renamed
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const fetchAll = async () => {
    try {
      const [f, s] = await Promise.all([
        axios.get(`${API}/api/admin/reach/folders`, { headers: tokenHeader() }),
        axios.get(`${API}/api/admin/reach/status`, { headers: tokenHeader() }),
      ]);
      setFolders(f.data.folders || []);
      setTotal(f.data.total || 0);
      setStatus(s.data);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Could not load Reach data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionStorage.getItem('admin_token')) { navigate('/admin'); return; }
    fetchAll();
    // Load discovery sector list once on mount
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/reach/discover/sectors`, { headers: tokenHeader() });
        setDiscoSectors(r.data?.sectors || []);
        setDiscoReady(!!r.data?.ready);
      } catch (err) {
        // Non-fatal
      }
    })();
  }, []); // eslint-disable-line

  const runDiscovery = async () => {
    if (!discoForm.sector) {
      toast({ title: 'Pick a sector', description: 'Choose a sector to find decision-makers in.', variant: 'destructive' });
      return;
    }
    setDiscovering(true);
    try {
      const r = await axios.post(`${API}/api/admin/reach/discover`, {
        sector: discoForm.sector,
        location: discoForm.location || undefined,
        target_count: Number(discoForm.target_count) || 50,
        folder: discoForm.folder || undefined,
      }, { headers: tokenHeader() });
      const inserted = r.data?.inserted || 0;
      const moved = r.data?.moved_existing || 0;
      const folder = r.data?.folder;
      toast({
        title: `${inserted + moved} lead${(inserted + moved) !== 1 ? 's' : ''} added`,
        description: `Folder: ${folder} · ${inserted} new, ${moved} already-existing moved.`,
      });
      setDiscoverOpen(false);
      fetchAll();
      if (folder) navigate(`/admin/reach/folders/${encodeURIComponent(folder)}`);
    } catch (err) {
      toast({ title: 'Discovery failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setDiscovering(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const slug = newName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, '');
    setCreateOpen(false);
    setNewName('');
    navigate(`/admin/reach/folders/${encodeURIComponent(slug)}`);
  };

  const handleDropOnFolder = async (e, folderName) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast({ title: 'CSV required', description: 'Drop a .csv file.', variant: 'destructive' });
      return;
    }
    setUploading(folderName);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(
        `${API}/api/admin/reach/folders/${encodeURIComponent(folderName)}/upload-csv`,
        fd,
        { headers: { ...tokenHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      const d = r.data;
      toast({
        title: `${d.imported} added to ${folderName}`,
        description: `${d.moved_existing} moved · ${d.skipped_suppressed} suppressed · ${d.skipped_invalid} invalid skipped`,
      });
      fetchAll();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setUploading(null);
    }
  };

  const startRename = (e, folder) => {
    e.preventDefault();
    e.stopPropagation();
    setRenaming(folder.name);
    setRenameValue(folder.name);
  };

  const saveRename = async (e) => {
    e?.stopPropagation?.();
    const cleaned = renameValue.trim();
    if (!cleaned || cleaned === renaming) {
      setRenaming(null);
      return;
    }
    setRenameSaving(true);
    try {
      await axios.post(
        `${API}/api/admin/reach/folders/${encodeURIComponent(renaming)}/rename`,
        { new_name: cleaned },
        { headers: tokenHeader() }
      );
      toast({ title: 'Renamed', description: `"${renaming}" → "${cleaned.toLowerCase().replace(/\s+/g, '-')}"` });
      setRenaming(null);
      setRenameValue('');
      fetchAll();
    } catch (err) {
      toast({ title: 'Rename failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setRenameSaving(false);
    }
  };

  const handleDeleteFolder = async (e, folder) => {
    e.preventDefault();
    e.stopPropagation();
    const choice = window.prompt(
      `Delete folder "${folder.name}" (${folder.count} lead${folder.count !== 1 ? 's' : ''})?\n\n` +
      `Type:\n  KEEP — to remove the folder but keep the leads (untag them)\n  DELETE — to delete the folder AND its leads permanently\n\n` +
      `Type KEEP or DELETE (or cancel):`
    );
    if (!choice) return;
    const upper = choice.trim().toUpperCase();
    if (upper !== 'KEEP' && upper !== 'DELETE') {
      toast({ title: 'Cancelled', description: 'Type exactly KEEP or DELETE.' });
      return;
    }
    try {
      const url = `${API}/api/admin/reach/folders/${encodeURIComponent(folder.name)}` + (upper === 'DELETE' ? '?also_delete_leads=true' : '');
      const r = await axios.delete(url, { headers: tokenHeader() });
      toast({
        title: upper === 'DELETE' ? `${r.data.deleted_leads || 0} leads deleted` : `${r.data.untagged_leads || 0} leads kept`,
        description: `Folder "${folder.name}" removed.`,
      });
      fetchAll();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return <ReachShell active={isOverview ? '/admin/reach' : '/admin/reach/folders'}><div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-[#e6007e]" /></div></ReachShell>;
  }

  return (
    <ReachShell active={isOverview ? '/admin/reach' : '/admin/reach/folders'}>
      {/* Health banner */}
      {status && !status.smtp_configured && (
        <div className="mb-6 rounded-xl border border-yellow-500/40 bg-yellow-500/10 p-4 flex items-start gap-3" data-testid="reach-smtp-warning">
          <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-white font-semibold">SMTP not configured — emails won't actually send.</p>
            <p className="text-xs text-gray-300 mt-0.5">Set up your SMTP credentials before sending.</p>
          </div>
          <Button onClick={() => navigate('/admin/reach/smtp')} size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-black">Set up SMTP</Button>
        </div>
      )}
      {status && status.smtp_configured && (
        <div className="mb-6 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center gap-3 text-sm">
          <CheckCircle className="w-4 h-4 text-emerald-400" />
          <span className="text-gray-300">Sending from <span className="text-white font-medium">{status.from_email}</span> · {status.templates} template{status.templates !== 1 ? 's' : ''} ready</span>
          {status.queued > 0 && <span className="text-yellow-300 ml-auto">{status.queued} email{status.queued !== 1 ? 's' : ''} in queue</span>}
        </div>
      )}

      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">{isOverview ? 'Overview' : 'Folders'}</h1>
          <p className="text-gray-400 text-sm">
            {isOverview
              ? 'Reach at a glance — your folders, send health, and templates.'
              : 'Drop a CSV into a folder. Open it. Send.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setDiscoverOpen(true)}
            className="bg-[#e6007e] hover:bg-[#c4006b] text-white"
            data-testid="reach-discover-btn"
            title="Auto-find UK SME decision-makers"
          >
            <Sparkles className="w-4 h-4 mr-2" />Discover leads
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm('Fill in any missing first names from the email address (e.g. tom.smith@x.com → Tom)?\n\nThis is safe — it only updates leads that currently have no first name and skips role inboxes like info@/sales@.')) return;
              try {
                const r = await axios.post(`${API}/api/admin/reach/leads/backfill-names`, {}, { headers: tokenHeader() });
                toast({ title: 'Names backfilled', description: `${r.data.updated} lead(s) updated · ${r.data.skipped} skipped (role inbox / no name found).` });
                fetchAll();
              } catch (err) {
                toast({ title: 'Backfill failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
              }
            }}
            className="border-gray-700 text-gray-200 hover:bg-gray-800"
            data-testid="reach-backfill-names-btn"
            title="Fill in missing first names from the email address"
          >
            Fix missing names
          </Button>
          <Button variant="outline" onClick={() => setCreateOpen(true)} className="border-gray-700 text-gray-200 hover:bg-gray-800" data-testid="reach-new-folder-btn">
            <FolderPlus className="w-4 h-4 mr-2" />New folder
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatCard label="Folders" value={folders.length} testid="reach-stat-folders" />
        <StatCard label="Total leads" value={total} />
        <StatCard label="Templates" value={status?.templates || 0} />
        <StatCard label="In queue" value={status?.queued || 0} accent={status?.queued > 0 ? 'yellow' : null} />
      </div>

      {/* Folders section heading on Overview only */}
      {isOverview && folders.length > 0 && (
        <div className="flex items-baseline justify-between mb-3 mt-2" data-testid="reach-overview-folders-heading">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Folder className="w-4 h-4 text-[#e6007e]" />
            Folders
          </h2>
          <button onClick={() => navigate('/admin/reach/folders')} className="text-xs text-gray-400 hover:text-white">
            View all →
          </button>
        </div>
      )}

      {folders.length === 0 ? (
        <Card className="bg-gray-800 border-gray-800">
          <CardContent className="py-16 text-center">
            <Inbox className="w-12 h-12 text-[#e6007e]/60 mx-auto mb-4" />
            <h3 className="text-white font-semibold text-lg mb-2">No folders yet</h3>
            <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">Create a folder, then upload a CSV of leads into it. That's it.</p>
            <Button onClick={() => setCreateOpen(true)} className="text-white" style={{ background: ACCENT }} data-testid="reach-empty-create-btn">
              <FolderPlus className="w-4 h-4 mr-2" />Create your first folder
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {folders.map((f) => (
            <Card
              key={f.name}
              className={`bg-gray-800 border-gray-800 hover:border-[#e6007e]/40 transition-all cursor-pointer group ${
                dragOver === f.name ? 'border-[#e6007e] ring-2 ring-[#e6007e]/40 scale-[1.02]' : ''
              } ${uploading === f.name ? 'opacity-60' : ''}`}
              onClick={() => navigate(`/admin/reach/folders/${encodeURIComponent(f.name)}`)}
              onDragOver={(e) => { e.preventDefault(); setDragOver(f.name); }}
              onDragLeave={() => setDragOver((d) => (d === f.name ? null : d))}
              onDrop={(e) => handleDropOnFolder(e, f.name)}
              data-testid={`reach-folder-card-${f.name}`}
            >
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${ACCENT}25`, color: ACCENT }}>
                      <Folder className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      {renaming === f.name ? (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Input
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveRename(e);
                              if (e.key === 'Escape') setRenaming(null);
                            }}
                            autoFocus
                            disabled={renameSaving}
                            className="h-8 text-sm bg-gray-700 border-gray-600"
                            data-testid={`reach-folder-rename-input-${f.name}`}
                          />
                          <button onClick={saveRename} disabled={renameSaving} className="text-emerald-400 hover:text-emerald-300 p-1" data-testid={`reach-folder-rename-save-${f.name}`}>
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setRenaming(null); }} className="text-gray-400 hover:text-white p-1">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-semibold capitalize truncate">{f.name}</h3>
                          <button
                            onClick={(e) => startRename(e, f)}
                            className="text-gray-400 hover:text-white shrink-0"
                            data-testid={`reach-folder-rename-btn-${f.name}`}
                            title="Rename folder"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      <p className="text-gray-500 text-xs">
                        {f.last_added ? new Date(f.last_added).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-white">{f.count}</span>
                    <button
                      onClick={(e) => handleDeleteFolder(e, f)}
                      className="text-gray-500 hover:text-red-400 p-1"
                      data-testid={`reach-folder-delete-btn-${f.name}`}
                      title="Delete folder"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {dragOver === f.name && (
                  <div className="rounded-md py-2 mb-2 text-center text-xs font-medium text-[#e6007e] bg-[#e6007e]/10 border border-dashed border-[#e6007e]/40">
                    Drop CSV to add to {f.name}
                  </div>
                )}
                {uploading === f.name && (
                  <div className="rounded-md py-2 mb-2 text-center text-xs font-medium text-gray-300 bg-gray-700/40">
                    Uploading…
                  </div>
                )}
                <Button
                  className="w-full text-white"
                  style={{ background: ACCENT }}
                  data-testid={`reach-folder-open-${f.name}`}
                >
                  <FolderOpen className="w-4 h-4 mr-2" />Open
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create folder dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white" data-testid="reach-create-folder-dialog">
          <DialogHeader>
            <DialogTitle>New folder</DialogTitle>
            <DialogDescription className="text-gray-400">e.g. Plumbers, Electricians, Cold Q1</DialogDescription>
          </DialogHeader>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Folder name" className="bg-gray-800 border-gray-700 mt-2" autoFocus data-testid="reach-create-folder-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-gray-700 text-gray-300">Cancel</Button>
            <Button onClick={handleCreate} className="text-white" style={{ background: ACCENT }} data-testid="reach-create-folder-confirm">Create & open</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discover Leads dialog */}
      <Dialog open={discoverOpen} onOpenChange={setDiscoverOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg" data-testid="reach-discover-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#e6007e]" />Discover decision-makers</DialogTitle>
            <DialogDescription className="text-gray-400">
              Pull verified UK SME owners/directors from Companies House + Apollo straight into a Reach folder.
            </DialogDescription>
          </DialogHeader>

          {!discoReady && (
            <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs text-yellow-200 flex items-start gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                Discovery keys aren't fully set up yet. Add them in <button onClick={() => { setDiscoverOpen(false); navigate('/admin/reach/settings'); }} className="underline hover:text-yellow-100">Reach → Settings</button> first.
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label className="text-gray-300 text-xs">Sector</Label>
              <Select value={discoForm.sector} onValueChange={(v) => setDiscoForm({ ...discoForm, sector: v })}>
                <SelectTrigger className="bg-gray-700 border-gray-600 mt-1" data-testid="reach-discover-sector"><SelectValue placeholder="Choose a sector" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  {discoSectors.map((s) => (
                    <SelectItem key={s.slug} value={s.slug}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Location (optional)</Label>
              <Input
                value={discoForm.location}
                onChange={(e) => setDiscoForm({ ...discoForm, location: e.target.value })}
                placeholder="e.g. Manchester, London — leave blank for all UK"
                className="bg-gray-700 border-gray-600 mt-1"
                data-testid="reach-discover-location"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-xs">How many leads?</Label>
                <Input
                  type="number"
                  min="10"
                  max="200"
                  value={discoForm.target_count}
                  onChange={(e) => setDiscoForm({ ...discoForm, target_count: e.target.value })}
                  className="bg-gray-700 border-gray-600 mt-1"
                  data-testid="reach-discover-target"
                />
                <p className="text-[10px] text-gray-500 mt-1">Max 200. Each lead ≈ 1 Apollo credit.</p>
              </div>
              <div>
                <Label className="text-gray-300 text-xs">Folder name (optional)</Label>
                <Input
                  value={discoForm.folder}
                  onChange={(e) => setDiscoForm({ ...discoForm, folder: e.target.value })}
                  placeholder="Auto-generated"
                  className="bg-gray-700 border-gray-600 mt-1"
                  data-testid="reach-discover-folder"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscoverOpen(false)} className="border-gray-700 text-gray-300" disabled={discovering}>Cancel</Button>
            <Button
              onClick={runDiscovery}
              disabled={discovering || !discoForm.sector || !discoReady}
              className="text-white"
              style={{ background: ACCENT }}
              data-testid="reach-discover-run-btn"
            >
              {discovering ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Searching…</> : <><Sparkles className="w-4 h-4 mr-2" />Discover {discoForm.target_count || 50} leads</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ReachShell>
  );
};

const StatCard = ({ label, value, accent, testid }) => (
  <div className="rounded-lg p-4 border border-gray-800 bg-gray-800" data-testid={testid}>
    <p className="text-xs text-gray-400">{label}</p>
    <p className={`text-2xl font-bold ${accent === 'yellow' ? 'text-yellow-400' : 'text-white'}`}>{value}</p>
  </div>
);

// ============== Folder Detail (leads + per-row actions) ==============
export const ReachFolderDetailPage = () => {
  const { folder } = useParams();
  const [leads, setLeads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', first_name: '', last_name: '', company_name: '' });
  const [adding, setAdding] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendMode, setSendMode] = useState('all'); // 'all' | 'selected'
  const [sendForm, setSendForm] = useState({ template_id: '', subject: '', body_text: '' });
  const [sending, setSending] = useState(false);
  const [emailLead, setEmailLead] = useState(null); // single-lead compose
  const [singleForm, setSingleForm] = useState({ template_id: '', subject: '', body_text: '' });
  const [singleSending, setSingleSending] = useState(false);
  const [aiTone, setAiTone] = useState(null); // currently rewriting tone
  const [signature, setSignature] = useState({ signature_html: '', logo_url: '' });
  const [attachSignature, setAttachSignature] = useState(true);
  const [attachSignatureBulk, setAttachSignatureBulk] = useState(true);
  const [lastSend, setLastSend] = useState(null);
  const [leadCounts, setLeadCounts] = useState({ new_count: 0, sent_count: 0 });
  const [filter, setFilter] = useState('all'); // 'all' | 'new' | 'sent'
  const fileRef = useRef();
  const navigate = useNavigate();
  const { toast } = useToast();

  const fetchLeads = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/reach/folders/${encodeURIComponent(folder)}/leads`, { headers: tokenHeader() });
      setLeads(r.data.leads || []);
      setLeadCounts({
        new_count: r.data.new_count || 0,
        sent_count: r.data.sent_count || 0,
      });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Could not load leads', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/outreach/templates`, { headers: tokenHeader() });
      setTemplates(r.data.templates || []);
    } catch { /* non-critical */ }
  };

  useEffect(() => { fetchLeads(); fetchTemplates(); fetchSignature(); }, [folder]); // eslint-disable-line

  const fetchSignature = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/reach/signature`, { headers: tokenHeader() });
      setSignature(r.data || { signature_html: '', logo_url: '' });
    } catch { /* non-critical */ }
  };

  const toggle = (id) => {
    const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(
        `${API}/api/admin/reach/folders/${encodeURIComponent(folder)}/upload-csv`,
        fd,
        { headers: { ...tokenHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      const d = r.data;
      toast({
        title: `${d.imported} added · ${d.moved_existing} moved`,
        description: `${d.skipped_suppressed} suppressed · ${d.skipped_invalid} invalid skipped`,
      });
      fetchLeads();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleAdd = async () => {
    if (!addForm.email) return;
    setAdding(true);
    try {
      await axios.post(
        `${API}/api/admin/reach/folders/${encodeURIComponent(folder)}/add-lead`,
        addForm,
        { headers: tokenHeader() }
      );
      toast({ title: 'Lead added' });
      setAddOpen(false);
      setAddForm({ email: '', first_name: '', last_name: '', company_name: '' });
      fetchLeads();
    } catch (err) {
      toast({ title: 'Could not add', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Delete ${email}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/reach/leads/${id}`, { headers: tokenHeader() });
      toast({ title: 'Deleted' });
      const n = new Set(selected); n.delete(id); setSelected(n);
      fetchLeads();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleMarkReplied = async (lead) => {
    const isReplied = lead.status === 'replied';
    try {
      const url = `${API}/api/admin/reach/leads/${lead.id}/${isReplied ? 'unmark-replied' : 'mark-replied'}`;
      await axios.post(url, {}, { headers: tokenHeader() });
      toast({ title: isReplied ? 'Mark removed' : `Marked ${lead.email} as replied` });
      fetchLeads();
    } catch (err) {
      toast({ title: 'Could not update', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const [eligiblePreview, setEligiblePreview] = useState(null);
  const [includeRecent, setIncludeRecent] = useState(false);

  const openBulkSend = async (mode) => {
    setSendMode(mode);
    setSendForm({ template_id: '', subject: '', body_text: '' });
    setAttachSignatureBulk(true);
    setIncludeRecent(false);
    setEligiblePreview(null);
    setSendOpen(true);
    // Fetch eligible-preview only for "send to all" mode (selected mode is explicit)
    if (mode === 'all') {
      try {
        const r = await axios.get(
          `${API}/api/admin/reach/folders/${encodeURIComponent(folder)}/eligible-preview?cooldown_days=14`,
          { headers: tokenHeader() }
        );
        setEligiblePreview(r.data);
      } catch (_) { /* non-fatal */ }
    }
  };

  const handleBulkSend = async () => {
    if (!sendForm.template_id && (!sendForm.subject || !sendForm.body_text)) {
      toast({ title: 'Pick a template, or type a subject + body.', variant: 'destructive' }); return;
    }
    setSending(true);
    try {
      const body = {
        ...sendForm,
        attach_signature: attachSignatureBulk,
        include_recent: includeRecent,
      };
      if (sendMode === 'selected') body.lead_ids = Array.from(selected);
      const r = await axios.post(
        `${API}/api/admin/reach/folders/${encodeURIComponent(folder)}/send`,
        body,
        { headers: tokenHeader() }
      );
      const d = r.data;
      setLastSend({ at: new Date(), ...d });
      toast({
        title: `${d.sent_now} sent`,
        description: d.message,
        duration: 6000,
      });
      setSendOpen(false);
      setSelected(new Set());
      fetchLeads();
    } catch (err) {
      toast({ title: 'Send failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const openSingleEmail = (lead) => {
    setEmailLead(lead);
    setSingleForm({ template_id: '', subject: '', body_text: '' });
  };

  // Sent-email history side panel state
  const [historyLead, setHistoryLead] = useState(null);
  const [historyEmails, setHistoryEmails] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeHistoryEmail, setActiveHistoryEmail] = useState(null); // currently expanded email

  const openSentHistory = async (lead) => {
    setHistoryLead(lead);
    setActiveHistoryEmail(null);
    setHistoryLoading(true);
    setHistoryEmails([]);
    try {
      const r = await axios.get(`${API}/api/admin/reach/leads/${lead.id}/sent-emails`, { headers: tokenHeader() });
      const items = r.data?.emails || [];
      setHistoryEmails(items);
      setActiveHistoryEmail(items[0] || null);
    } catch (err) {
      toast({ title: 'Could not load sent emails', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSingleSend = async () => {
    if (!singleForm.template_id && (!singleForm.subject || !singleForm.body_text)) {
      toast({ title: 'Pick a template, or type a subject + body.', variant: 'destructive' }); return;
    }
    setSingleSending(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/reach/leads/${emailLead.id}/send`,
        { ...singleForm, attach_signature: attachSignature },
        { headers: tokenHeader() }
      );
      const d = r.data;
      toast({
        title: d.sent_now === 1 ? `Sent to ${emailLead.email}` : `Queued for ${emailLead.email}`,
        description: d.message,
      });
      setEmailLead(null);
    } catch (err) {
      toast({ title: 'Could not send', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSingleSending(false);
    }
  };

  const renderTokens = (text, lead) => {
    if (!text || !lead) return text || '';
    const fn = lead.first_name || (lead.full_name ? String(lead.full_name).split(' ')[0] : '');
    const ln = lead.last_name || (lead.full_name ? String(lead.full_name).split(' ').slice(1).join(' ') : '');
    const fullName = lead.full_name || [fn, ln].filter(Boolean).join(' ');
    const fnGreeting = fn || 'there';
    const company = lead.company_name || 'your business';

    let s = String(text)
      // {{...}} style
      .replace(/{{\s*first_name\s*}}/gi, fnGreeting)
      .replace(/{{\s*last_name\s*}}/gi, ln || '')
      .replace(/{{\s*full_name\s*}}/gi, fullName || fnGreeting)
      .replace(/{{\s*name\s*}}/gi, fnGreeting)
      .replace(/{{\s*company_name\s*}}/gi, company)
      .replace(/{{\s*company\s*}}/gi, company)
      .replace(/{{\s*email\s*}}/gi, lead.email || '');
    // [Token] style
    const bracketMap = {
      name: fnGreeting,
      firstname: fnGreeting,
      'first name': fnGreeting,
      first_name: fnGreeting,
      lastname: ln || '',
      'last name': ln || '',
      last_name: ln || '',
      fullname: fullName || fnGreeting,
      'full name': fullName || fnGreeting,
      full_name: fullName || fnGreeting,
      company: company,
      companyname: company,
      'company name': company,
      company_name: company,
      email: lead.email || '',
    };
    s = s.replace(/\[([^\[\]\n]{1,40})\]/g, (m, inner) => {
      const k = inner.trim().toLowerCase();
      return Object.prototype.hasOwnProperty.call(bracketMap, k) ? bracketMap[k] : m;
    });
    return s;
  };

  // Convert template HTML body to plain text while preserving paragraph
  // breaks ( </p>, <br>, </div> ) — so a textarea can show the content
  // correctly with blank lines between paragraphs.
  const htmlBodyToPlainText = (html) => {
    if (!html) return '';
    let s = html;
    // Closing block tags → double newline (paragraph break)
    s = s.replace(/<\/(p|div|h[1-6]|li)\s*>/gi, '\n\n');
    // <br> → single newline
    s = s.replace(/<\s*br\s*\/?\s*>/gi, '\n');
    // Strip every other tag
    s = s.replace(/<[^>]+>/g, '');
    // Decode common entities
    s = s.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
    // Collapse runs of 3+ newlines down to a maximum of 2 (one blank line)
    s = s.replace(/\n{3,}/g, '\n\n').trim();
    return s;
  };

  const onTemplatePick = (id, formSetter, currentForm, leadForRender) => {
    const t = templates.find((x) => x.id === id);
    const rawSubject = t?.subject || currentForm.subject || '';
    const rawBody = t?.body_text || currentForm.body_text || (t?.body_html ? htmlBodyToPlainText(t.body_html) : '');
    formSetter({
      ...currentForm,
      template_id: id,
      subject: leadForRender ? renderTokens(rawSubject, leadForRender) : rawSubject,
      body_text: leadForRender ? renderTokens(rawBody, leadForRender) : rawBody,
    });
  };

  const handleAiRewrite = async (tone) => {
    if (!singleForm.body_text) {
      toast({ title: 'Nothing to rewrite', description: 'Pick a template or type something first.', variant: 'destructive' });
      return;
    }
    setAiTone(tone);
    try {
      const r = await axios.post(`${API}/api/admin/reach/ai-rewrite`, {
        body: singleForm.body_text,
        tone,
        subject: singleForm.subject,
        rewrite_subject: false,
      }, { headers: tokenHeader() });
      setSingleForm({ ...singleForm, body_text: r.data.body });
      toast({ title: `Rewritten in ${tone} tone`, description: 'Edit if needed, then send.' });
    } catch (err) {
      toast({ title: 'AI rewrite failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setAiTone(null);
    }
  };

  if (loading) {
    return <ReachShell active="/admin/reach"><div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-[#e6007e]" /></div></ReachShell>;
  }

  return (
    <ReachShell active="/admin/reach">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap" data-testid="reach-folder-detail">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/admin/reach')} className="text-gray-400 hover:text-white shrink-0">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${ACCENT}25`, color: ACCENT }}>
            <FolderOpen className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-white capitalize truncate">{folder}</h1>
            <p className="text-gray-400 text-sm">
              {leads.length} lead{leads.length !== 1 ? 's' : ''}
              {leads.length > 0 && (
                <>
                  {' '}·{' '}
                  <span className="text-emerald-300" data-testid="reach-folder-new-count">{leadCounts.new_count} new</span>
                  {' '}·{' '}
                  <span className="text-gray-400" data-testid="reach-folder-sent-count">{leadCounts.sent_count} mailed</span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={handleUpload} />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-gray-700 text-gray-200 hover:bg-gray-800" data-testid="reach-upload-btn">
            <Upload className="w-4 h-4 mr-2" />{uploading ? 'Uploading…' : 'Upload CSV'}
          </Button>
          <Button variant="outline" onClick={() => setAddOpen(true)} className="border-gray-700 text-gray-200 hover:bg-gray-800" data-testid="reach-add-lead-btn">
            <Plus className="w-4 h-4 mr-2" />Add lead
          </Button>
          <Button onClick={() => openBulkSend('all')} disabled={leads.length === 0} className="text-white" style={{ background: ACCENT }} data-testid="reach-send-all-btn">
            <Send className="w-4 h-4 mr-2" />Send to all {leads.length}
          </Button>
        </div>
      </div>

      {lastSend && (
        <div className="mb-6 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 flex items-start gap-3" data-testid="reach-last-send-banner">
          <CheckCircle className="w-5 h-5 text-emerald-300 mt-0.5" />
          <div className="flex-1">
            <p className="text-white font-semibold">{lastSend.sent_now} sent · {lastSend.still_queued || 0} queued · {lastSend.sent_failed || 0} failed</p>
            <p className="text-xs text-gray-300 mt-0.5">{lastSend.message} · {new Date(lastSend.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          <button onClick={() => setLastSend(null)} className="text-gray-400 hover:text-white"><XCircle className="w-4 h-4" /></button>
        </div>
      )}

      {selected.size > 0 && (
        <div className="mb-4 flex items-center gap-3 p-3 rounded-lg border border-[#e6007e]/30 bg-[#e6007e]/10" data-testid="reach-bulk-bar">
          <span className="text-white font-medium">{selected.size} selected</span>
          <Button size="sm" onClick={() => openBulkSend('selected')} className="text-white" style={{ background: ACCENT }} data-testid="reach-send-selected-btn">
            <Send className="w-4 h-4 mr-2" />Send to selected
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} className="border-gray-700 text-gray-200 hover:bg-gray-800">
            Clear
          </Button>
        </div>
      )}

      {leads.length === 0 ? (
        <Card className="bg-gray-800 border-gray-800">
          <CardContent className="py-16 text-center">
            <Inbox className="w-12 h-12 text-[#e6007e]/60 mx-auto mb-4" />
            <h3 className="text-white font-semibold text-lg mb-2">Empty folder</h3>
            <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">Upload a CSV with at least an <span className="text-white font-mono text-xs">email</span> column, or add one lead at a time.</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => fileRef.current?.click()} className="text-white" style={{ background: ACCENT }}>
                <Upload className="w-4 h-4 mr-2" />Upload CSV
              </Button>
              <Button variant="outline" onClick={() => setAddOpen(true)} className="border-gray-700 text-gray-200 hover:bg-gray-800">
                <Plus className="w-4 h-4 mr-2" />Add lead manually
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (() => {
        // Filter chips: All / New / Mailed
        const filteredLeads = leads.filter((l) => {
          if (filter === 'new') return !l.last_sent_at;
          if (filter === 'sent') return !!l.last_sent_at;
          return true;
        });

        const fmtAgo = (iso) => {
          if (!iso) return '';
          try {
            const d = new Date(iso);
            const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
            if (mins < 1) return 'just now';
            if (mins < 60) return `${mins}m ago`;
            const hrs = Math.round(mins / 60);
            if (hrs < 24) return `${hrs}h ago`;
            const days = Math.round(hrs / 24);
            if (days < 30) return `${days}d ago`;
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
          } catch { return ''; }
        };

        const FilterChip = ({ id, label, count, color }) => (
          <button
            type="button"
            onClick={() => setFilter(id)}
            data-testid={`reach-filter-${id}`}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              filter === id
                ? 'bg-[#e6007e] text-white border-[#e6007e]'
                : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-600'
            }`}
          >
            {label} <span className={filter === id ? 'opacity-80' : color || 'text-gray-500'}>({count})</span>
          </button>
        );

        return (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="reach-filter-chips">
              <FilterChip id="all" label="All" count={leads.length} />
              <FilterChip id="new" label="New" count={leadCounts.new_count} color="text-emerald-300" />
              <FilterChip id="sent" label="Mailed" count={leadCounts.sent_count} color="text-gray-400" />
            </div>

            {filteredLeads.length === 0 ? (
              <Card className="bg-gray-800 border-gray-800">
                <CardContent className="py-12 text-center text-sm text-gray-400">
                  No leads in this view. Try a different filter.
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gray-800 border-gray-800">
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-900/50 border-b border-gray-800">
                      <tr>
                        <th className="text-left p-3 w-10">
                          <Checkbox
                            checked={selected.size === filteredLeads.length && filteredLeads.length > 0}
                            onCheckedChange={() => {
                              if (selected.size === filteredLeads.length) setSelected(new Set());
                              else setSelected(new Set(filteredLeads.map((x) => x.id)));
                            }}
                            data-testid="reach-select-all"
                          />
                        </th>
                        <th className="text-left p-3 text-gray-400 font-medium">Email</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Name</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Company</th>
                        <th className="text-right p-3 text-gray-400 font-medium pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {filteredLeads.map((l) => (
                        <tr key={l.id} className={`hover:bg-gray-900/30 ${selected.has(l.id) ? 'bg-[#e6007e]/5' : ''}`} data-testid={`reach-lead-row-${l.id}`}>
                          <td className="p-3">
                            <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggle(l.id)} data-testid={`reach-lead-checkbox-${l.id}`} />
                          </td>
                    <td className="p-3 text-white font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{l.email}</span>
                        {l.last_sent_at && l.status !== 'replied' && l.status !== 'bounced' && l.status !== 'unsubscribed' && (
                          <span
                            className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                            title={l.last_sent_subject ? `Last sent: "${l.last_sent_subject}"` : 'Already mailed'}
                            data-testid={`reach-lead-sent-pill-${l.id}`}
                          >
                            ✓ Sent · {fmtAgo(l.last_sent_at)}
                          </span>
                        )}
                        {l.status === 'replied' && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30" data-testid={`reach-lead-replied-pill-${l.id}`}>
                            Replied
                          </span>
                        )}
                        {l.status === 'unsubscribed' && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30">
                            Unsub
                          </span>
                        )}
                        {l.status === 'bounced' && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-300 border border-orange-500/30">
                            Bounced
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-gray-300">{[l.first_name, l.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td className="p-3 text-gray-300">{l.company_name || '—'}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openSingleEmail(l)} className="text-[#e6007e] hover:text-white hover:bg-[#e6007e]/10 h-8 px-2" data-testid={`reach-lead-email-btn-${l.id}`}>
                          <Mail className="w-4 h-4 mr-1" />Email
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openSentHistory(l)}
                          className="text-blue-300 hover:text-white hover:bg-blue-500/10 h-8 px-2"
                          title="View what was actually sent to this lead"
                          data-testid={`reach-lead-history-btn-${l.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleMarkReplied(l)}
                          className={`h-8 px-2 ${l.status === 'replied' ? 'text-purple-300 hover:text-white hover:bg-purple-500/10' : 'text-gray-500 hover:text-purple-300 hover:bg-purple-500/10'}`}
                          title={l.status === 'replied' ? 'Unmark as replied' : 'Mark as replied'}
                          data-testid={`reach-lead-replied-btn-${l.id}`}
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(l.id, l.email)} className="text-gray-500 hover:text-red-300 hover:bg-red-500/10 h-8 px-2" data-testid={`reach-lead-delete-btn-${l.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
            )}
          </>
        );
      })()}

      {/* Add lead dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="capitalize">Add lead to "{folder}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="email@example.com" className="bg-gray-800 border-gray-700" data-testid="reach-add-email" autoFocus />
            <div className="grid grid-cols-2 gap-2">
              <Input value={addForm.first_name} onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })} placeholder="First name" className="bg-gray-800 border-gray-700" />
              <Input value={addForm.last_name} onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })} placeholder="Last name" className="bg-gray-800 border-gray-700" />
            </div>
            <Input value={addForm.company_name} onChange={(e) => setAddForm({ ...addForm, company_name: e.target.value })} placeholder="Company" className="bg-gray-800 border-gray-700" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="border-gray-700 text-gray-300">Cancel</Button>
            <Button onClick={handleAdd} disabled={adding || !addForm.email} className="text-white" style={{ background: ACCENT }} data-testid="reach-add-confirm">
              {adding ? 'Adding…' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk send dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl" data-testid="reach-send-dialog">
          <DialogHeader>
            <DialogTitle>Send to {sendMode === 'all' ? `all ${leads.length}` : `${selected.size} selected`} lead{(sendMode === 'all' ? leads.length : selected.size) !== 1 ? 's' : ''}</DialogTitle>
            <DialogDescription className="text-gray-400">Pick a template, or type your own. Fires immediately, bypassing the send window.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300 text-xs">Template (optional)</Label>
              <Select value={sendForm.template_id} onValueChange={(v) => onTemplatePick(v, setSendForm, sendForm)}>
                <SelectTrigger className="bg-gray-800 border-gray-700 mt-1" data-testid="reach-send-template-select">
                  <SelectValue placeholder={templates.length === 0 ? 'No templates yet' : 'Pick a template'} />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Subject *</Label>
              <Input value={sendForm.subject} onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })} placeholder="Quick one for {{company_name}}" className="bg-gray-800 border-gray-700 mt-1" data-testid="reach-send-subject" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Body *</Label>
              <Textarea value={sendForm.body_text} onChange={(e) => setSendForm({ ...sendForm, body_text: e.target.value })} placeholder="Hi {{first_name}}, ..." rows={8} className="bg-gray-800 border-gray-700 mt-1 font-mono text-sm" data-testid="reach-send-body" />
              <p className="text-xs text-gray-500 mt-1">Tokens: {'{{first_name}} {{last_name}} {{company_name}}'} or [Name] [Company]</p>
            </div>

            {/* Signature preview + toggle */}
            <div className="border border-gray-700 rounded-lg p-3 bg-gray-800/40" data-testid="reach-bulk-signature-block">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-gray-300 text-sm font-medium flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[#e6007e]" />
                  Attach signature & logo to all recipients
                </span>
                <input
                  type="checkbox"
                  checked={attachSignatureBulk}
                  onChange={(e) => setAttachSignatureBulk(e.target.checked)}
                  className="w-4 h-4 accent-[#e6007e]"
                  data-testid="reach-bulk-signature-toggle"
                />
              </label>
              {attachSignatureBulk && (signature.signature_html || signature.logo_url) ? (
                <div className="mt-3 pt-3 border-t border-gray-700 text-sm text-gray-200 [&_ul]:list-none [&_ul]:p-0 [&_ul]:m-0 [&_ol]:list-none [&_ol]:p-0 [&_ol]:m-0 [&_li]:list-none [&_li]:ml-0" data-testid="reach-bulk-signature-preview">
                  {signature.signature_html && (
                    <div dangerouslySetInnerHTML={{ __html: signature.signature_html }} />
                  )}
                  {signature.logo_url && (
                    <img src={signature.logo_url} alt="Logo" width="120" style={{ width: '120px', maxWidth: '120px', height: 'auto', marginTop: '14px', display: 'block' }} />
                  )}
                </div>
              ) : attachSignatureBulk ? (
                <p className="mt-2 text-xs text-yellow-400">No signature configured. Add one in SMTP → Email signature.</p>
              ) : (
                <p className="mt-2 text-xs text-gray-500">Signature off for this batch only. Default is on.</p>
              )}
            </div>
          </div>
          <DialogFooter className="flex-col items-stretch sm:flex-row sm:items-center sm:gap-2">
            {sendMode === 'all' && eligiblePreview && (
              <div className="flex-1 text-xs mr-auto" data-testid="reach-eligible-preview">
                {eligiblePreview.skipped_recent > 0 ? (
                  <>
                    <div className="text-gray-300">
                      Will send to <span className="text-emerald-300 font-semibold">{includeRecent ? eligiblePreview.total_in_folder : eligiblePreview.eligible}</span> of {eligiblePreview.total_in_folder}
                      {!includeRecent && (
                        <span className="text-gray-500"> ({eligiblePreview.skipped_recent} mailed in last {eligiblePreview.cooldown_days}d)</span>
                      )}
                    </div>
                    <label className="inline-flex items-center gap-2 mt-1 cursor-pointer text-yellow-300 hover:text-yellow-200">
                      <input
                        type="checkbox"
                        checked={includeRecent}
                        onChange={(e) => setIncludeRecent(e.target.checked)}
                        className="w-3.5 h-3.5 accent-[#e6007e]"
                        data-testid="reach-include-recent-toggle"
                      />
                      Include the {eligiblePreview.skipped_recent} mailed recently (re-send)
                    </label>
                  </>
                ) : (
                  <span className="text-gray-400">{eligiblePreview.eligible} eligible recipient{eligiblePreview.eligible !== 1 ? 's' : ''}.</span>
                )}
              </div>
            )}
            <Button variant="outline" onClick={() => setSendOpen(false)} className="border-gray-700 text-gray-300">Cancel</Button>
            <Button onClick={handleBulkSend} disabled={sending} className="text-white" style={{ background: ACCENT }} data-testid="reach-send-confirm">
              <Send className="w-4 h-4 mr-2" />
              {sending
                ? 'Sending…'
                : sendMode === 'all'
                  ? `Send to ${(includeRecent || !eligiblePreview) ? (eligiblePreview?.total_in_folder ?? leads.length) : eligiblePreview.eligible} now`
                  : `Send to ${selected.size} now`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sent-email history viewer */}
      <Dialog open={!!historyLead} onOpenChange={(o) => !o && setHistoryLead(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-4xl" data-testid="reach-history-dialog">
          {historyLead && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-300" />
                  Sent emails for {historyLead.email}
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  Exactly what landed in their inbox — signature, logo, paragraphs included.
                </DialogDescription>
              </DialogHeader>
              {historyLoading ? (
                <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-[#e6007e]" /></div>
              ) : historyEmails.length === 0 ? (
                <div className="text-center py-10 text-gray-400 text-sm" data-testid="reach-history-empty">
                  No emails sent to this lead yet.
                </div>
              ) : (
                <div className="grid grid-cols-12 gap-3" style={{ minHeight: 420 }}>
                  {/* Left rail: list of sends */}
                  <div className="col-span-4 border border-gray-700 rounded-lg overflow-y-auto" style={{ maxHeight: 480 }}>
                    {historyEmails.map((e) => {
                      const isActive = activeHistoryEmail?.id === e.id;
                      const statusColor = {
                        sent: 'text-emerald-300',
                        delivered: 'text-emerald-300',
                        opened: 'text-emerald-400',
                        replied: 'text-purple-300',
                        bounced: 'text-orange-300',
                        failed: 'text-red-300',
                        queued: 'text-yellow-300',
                      }[e.status] || 'text-gray-300';
                      return (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => setActiveHistoryEmail(e)}
                          className={`w-full text-left px-3 py-2.5 border-b border-gray-700 last:border-b-0 hover:bg-gray-900/40 transition ${isActive ? 'bg-[#e6007e]/10 border-l-2 border-l-[#e6007e]' : ''}`}
                          data-testid={`reach-history-item-${e.id}`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <span className={`text-[10px] uppercase tracking-wide font-semibold ${statusColor}`}>{e.status}</span>
                            <span className="text-[10px] text-gray-500 font-mono">
                              {e.sent_at ? new Date(e.sent_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </span>
                          </div>
                          <div className="text-sm text-white truncate">{e.subject || '(no subject)'}</div>
                          {e.opened_at && <div className="text-[10px] text-emerald-400 mt-0.5">Opened</div>}
                          {e.replied_at && <div className="text-[10px] text-purple-300 mt-0.5">Replied</div>}
                          {e.error_message && <div className="text-[10px] text-red-400 mt-0.5 truncate" title={e.error_message}>Error: {e.error_message.slice(0, 60)}</div>}
                        </button>
                      );
                    })}
                  </div>
                  {/* Right pane: rendered email body */}
                  <div className="col-span-8 border border-gray-700 rounded-lg overflow-hidden bg-white flex flex-col">
                    {activeHistoryEmail ? (
                      <>
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs">
                          <div className="font-semibold text-gray-700 truncate">{activeHistoryEmail.subject}</div>
                          <div className="text-gray-500">To: {activeHistoryEmail.to_email}</div>
                        </div>
                        <iframe
                          title="Sent email preview"
                          srcDoc={activeHistoryEmail.body_html || `<pre style="font-family:ui-monospace,monospace;padding:16px;white-space:pre-wrap;color:#374151;">${(activeHistoryEmail.body_text || '(no body)').replace(/[<>&]/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;'}[c]))}</pre>`}
                          sandbox=""
                          className="flex-1 w-full border-0"
                          style={{ minHeight: 380, background: 'white' }}
                          data-testid="reach-history-body-iframe"
                        />
                      </>
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Pick an email on the left.</div>
                    )}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setHistoryLead(null)} className="border-gray-700 text-gray-300">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Single-lead compose */}
      <Dialog open={!!emailLead} onOpenChange={(o) => !o && setEmailLead(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl" data-testid="reach-single-email-dialog">
          {emailLead && (
            <>
              <DialogHeader>
                <DialogTitle>Email {emailLead.email}</DialogTitle>
                <DialogDescription className="text-gray-400">
                  {[emailLead.first_name, emailLead.last_name].filter(Boolean).join(' ')} {emailLead.company_name ? `· ${emailLead.company_name}` : ''}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300 text-xs">Template (optional)</Label>
                  <Select value={singleForm.template_id} onValueChange={(v) => onTemplatePick(v, setSingleForm, singleForm, emailLead)}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 mt-1"><SelectValue placeholder="Pick a template — auto-filled with their name" /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#e6007e]/80 mt-1">
                    Tokens auto-filled for <span className="text-white font-medium">{emailLead.first_name || emailLead.email.split('@')[0]}{emailLead.company_name ? ` · ${emailLead.company_name}` : ''}</span>
                  </p>
                </div>
                <div>
                  <Label className="text-gray-300 text-xs">Subject</Label>
                  <Input value={singleForm.subject} onChange={(e) => setSingleForm({ ...singleForm, subject: e.target.value })} className="bg-gray-800 border-gray-700 mt-1" data-testid="reach-single-subject" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-gray-300 text-xs">Body</Label>
                    <div className="flex items-center gap-1" data-testid="reach-ai-rewrite-bar">
                      <span className="text-xs text-gray-500 mr-1">AI rewrite:</span>
                      {['curious', 'direct', 'playful', 'formal'].map((tone) => (
                        <Button
                          key={tone}
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={aiTone !== null || !singleForm.body_text}
                          onClick={() => handleAiRewrite(tone)}
                          className="h-6 px-2 text-xs text-[#e6007e] hover:text-white hover:bg-[#e6007e]/10"
                          data-testid={`reach-ai-rewrite-${tone}`}
                        >
                          <Sparkles className="w-3 h-3 mr-1" />
                          {aiTone === tone ? '...' : tone}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <Textarea value={singleForm.body_text} onChange={(e) => setSingleForm({ ...singleForm, body_text: e.target.value })} rows={10} className="bg-gray-800 border-gray-700 mt-1 font-mono text-sm" data-testid="reach-single-body" />
                </div>

                {/* Signature preview + toggle */}
                <div className="border border-gray-700 rounded-lg p-3 bg-gray-800/40" data-testid="reach-single-signature-block">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-gray-300 text-sm font-medium flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-[#e6007e]" />
                      Attach signature & logo
                    </span>
                    <input
                      type="checkbox"
                      checked={attachSignature}
                      onChange={(e) => setAttachSignature(e.target.checked)}
                      className="w-4 h-4 accent-[#e6007e]"
                      data-testid="reach-single-signature-toggle"
                    />
                  </label>
                  {attachSignature && (signature.signature_html || signature.logo_url) ? (
                    <div className="mt-3 pt-3 border-t border-gray-700 text-sm text-gray-200 [&_ul]:list-none [&_ul]:p-0 [&_ul]:m-0 [&_ol]:list-none [&_ol]:p-0 [&_ol]:m-0 [&_li]:list-none [&_li]:ml-0" data-testid="reach-single-signature-preview">
                      {signature.signature_html && (
                        <div dangerouslySetInnerHTML={{ __html: renderTokens(signature.signature_html, emailLead) }} />
                      )}
                      {signature.logo_url && (
                        <img src={signature.logo_url} alt="Logo" width="120" style={{ width: '120px', maxWidth: '120px', height: 'auto', marginTop: '14px', display: 'block' }} />
                      )}
                    </div>
                  ) : attachSignature ? (
                    <p className="mt-2 text-xs text-yellow-400">No signature configured. Add one in SMTP → Email signature.</p>
                  ) : (
                    <p className="mt-2 text-xs text-gray-500">Signature off for this email only. Default is on.</p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEmailLead(null)} className="border-gray-700 text-gray-300">Cancel</Button>
                <Button onClick={handleSingleSend} disabled={singleSending} className="text-white" style={{ background: ACCENT }} data-testid="reach-single-send-confirm">
                  <Send className="w-4 h-4 mr-2" />
                  {singleSending ? 'Sending…' : 'Send now'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </ReachShell>
  );
};

// ============== Settings (reset data) ==============
export const ReachSettingsPage = () => {
  const [resetting, setResetting] = useState(false);
  const [discoStatus, setDiscoStatus] = useState(null);
  const [apolloKey, setApolloKey] = useState('');
  const [chKey, setChKey] = useState('');
  const [savingKeys, setSavingKeys] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/reach/discover/sectors`, { headers: tokenHeader() });
        setDiscoStatus(r.data);
      } catch (err) {
        // Non-fatal
      }
    })();
  }, []);

  const saveDiscoveryKeys = async () => {
    if (!apolloKey && !chKey) {
      toast({ title: 'Nothing to save', description: 'Enter at least one key.', variant: 'destructive' });
      return;
    }
    setSavingKeys(true);
    try {
      await axios.post(`${API}/api/admin/reach/discover/keys`, {
        apollo_api_key: apolloKey || undefined,
        companies_house_api_key: chKey || undefined,
      }, { headers: tokenHeader() });
      toast({ title: 'Discovery keys saved', description: 'You can now run the Discover Leads agent on the Folders page.' });
      setApolloKey('');
      setChKey('');
      const r = await axios.get(`${API}/api/admin/reach/discover/sectors`, { headers: tokenHeader() });
      setDiscoStatus(r.data);
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSavingKeys(false);
    }
  };

  const handleReset = async (alsoTemplates, alsoSuppressions) => {
    const msg = `This will permanently delete:\n\n• All leads\n• All folders\n• All sent/queued emails\n• All campaigns & history${alsoTemplates ? '\n• All templates' : ''}${alsoSuppressions ? '\n• Unsubscribe list' : ''}\n\nContinue?`;
    if (!window.confirm(msg)) return;
    if (!window.confirm("Last chance — this can't be undone. Reset Reach data?")) return;
    setResetting(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/reach/reset-data`,
        { also_templates: alsoTemplates, also_suppressions: alsoSuppressions },
        { headers: tokenHeader() }
      );
      const d = r.data.deleted || {};
      const total = Object.values(d).reduce((s, n) => s + n, 0);
      toast({ title: `${total} record${total !== 1 ? 's' : ''} deleted`, description: 'Reach is back to a clean slate.' });
      navigate('/admin/reach');
    } catch (err) {
      toast({ title: 'Reset failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setResetting(false);
    }
  };

  return (
    <ReachShell active="/admin/reach/settings">
      <h1 className="text-2xl font-bold text-white mb-6">Settings</h1>

      {/* Discovery agent keys */}
      <Card className="bg-gray-800 border-gray-700 max-w-2xl mb-6">
        <CardContent className="pt-6">
          <h3 className="text-white font-semibold flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-[#e6007e]" />Discovery agent
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            Auto-find decision-makers at UK SMEs by sector and region. Powered by{' '}
            <a href="https://www.apollo.io/sign-up" target="_blank" rel="noreferrer" className="text-[#e6007e] hover:underline">Apollo.io</a>{' '}(free 100 credits/mo) +{' '}
            <a href="https://developer.company-information.service.gov.uk/" target="_blank" rel="noreferrer" className="text-[#e6007e] hover:underline">Companies House</a>{' '}(free, UK gov).
          </p>
          {discoStatus && (
            <div className="mb-4 flex flex-wrap gap-2 text-xs">
              {discoStatus.apollo_configured ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"><CheckCircle className="w-3.5 h-3.5" />Apollo connected</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-yellow-500/10 text-yellow-300 border border-yellow-500/30"><AlertTriangle className="w-3.5 h-3.5" />Apollo key missing</span>
              )}
              {discoStatus.companies_house_configured ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"><CheckCircle className="w-3.5 h-3.5" />Companies House connected</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-yellow-500/10 text-yellow-300 border border-yellow-500/30"><AlertTriangle className="w-3.5 h-3.5" />Companies House key missing</span>
              )}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label className="text-gray-300 text-xs">Apollo.io API key</Label>
              <Input
                type="password"
                value={apolloKey}
                onChange={(e) => setApolloKey(e.target.value)}
                placeholder={discoStatus?.apollo_configured ? '•••••••• (already set — paste new key to replace)' : 'Paste from Apollo → Settings → Integrations → API'}
                className="bg-gray-700 border-gray-600 mt-1 text-white"
                data-testid="reach-apollo-key-input"
              />
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Companies House API key</Label>
              <Input
                type="password"
                value={chKey}
                onChange={(e) => setChKey(e.target.value)}
                placeholder={discoStatus?.companies_house_configured ? '•••••••• (already set — paste new key to replace)' : 'Register at developer.company-information.service.gov.uk'}
                className="bg-gray-700 border-gray-600 mt-1 text-white"
                data-testid="reach-ch-key-input"
              />
            </div>
            <Button
              onClick={saveDiscoveryKeys}
              disabled={savingKeys}
              className="bg-[#e6007e] hover:bg-[#c4006b] text-white"
              data-testid="reach-save-discovery-keys-btn"
            >
              {savingKeys ? 'Saving…' : 'Save discovery keys'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gray-800 border-red-500/30 max-w-2xl">
        <CardContent className="pt-6">
          <h3 className="text-white font-semibold flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />Reset Reach data
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            Wipe all leads, folders, queued/sent emails, campaigns and send history. SMTP, templates and unsubscribe list are kept by default.
          </p>
          <div className="flex flex-col gap-2">
            <Button variant="outline" onClick={() => handleReset(false, false)} disabled={resetting} className="border-red-500/40 text-red-300 hover:bg-red-500/10 justify-start" data-testid="reach-reset-data-btn">
              <Trash2 className="w-4 h-4 mr-2" />Reset leads, emails & history (keep templates + SMTP)
            </Button>
            <Button variant="outline" onClick={() => handleReset(true, false)} disabled={resetting} className="border-red-500/40 text-red-300 hover:bg-red-500/10 justify-start text-xs" data-testid="reach-reset-with-templates-btn">
              <Trash2 className="w-4 h-4 mr-2" />…also delete templates
            </Button>
            <Button variant="outline" onClick={() => handleReset(true, true)} disabled={resetting} className="border-red-500/40 text-red-300 hover:bg-red-500/10 justify-start text-xs" data-testid="reach-reset-everything-btn">
              <Trash2 className="w-4 h-4 mr-2" />…also clear unsubscribe list (factory reset)
            </Button>
          </div>
        </CardContent>
      </Card>
    </ReachShell>
  );
};

// Templates and SMTP redirect to the existing well-built admin pages — no iframe wrapper.
export const ReachTemplatesPage = () => {
  const navigate = useNavigate();
  useEffect(() => { navigate('/admin/outreach/templates?from=reach', { replace: true }); }, []); // eslint-disable-line
  return null;
};

export const ReachSMTPPage = () => {
  const navigate = useNavigate();
  useEffect(() => { navigate('/admin/outreach/settings?from=reach', { replace: true }); }, []); // eslint-disable-line
  return null;
};

export const ReachReferralsPage = () => {
  const navigate = useNavigate();
  useEffect(() => { navigate('/admin/outreach/referrals?from=reach', { replace: true }); }, []); // eslint-disable-line
  return null;
};

// Legacy /admin/outreach lands here — redirect into the new Reach experience
export const OutreachLegacyRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => { navigate('/admin/reach', { replace: true }); }, []); // eslint-disable-line
  return null;
};

// Analytics — the lead pipeline + email metrics the user wants out of the way
export const ReachAnalyticsPage = () => {
  const [stats, setStats] = useState(null);
  const [scanInfo, setScanInfo] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const fetchStats = async () => {
    try {
      const [a, s, h] = await Promise.all([
        axios.get(`${API}/api/admin/reach/analytics-detailed`, { headers: tokenHeader() }),
        axios.get(`${API}/api/admin/reach/scan-status`, { headers: tokenHeader() }),
        axios.get(`${API}/api/admin/reach/history?limit=10`, { headers: tokenHeader() }),
      ]);
      setStats(a.data);
      setScanInfo(s.data);
      setHistory(h.data.history || []);
    } catch (err) {
      toast({ title: 'Could not load analytics', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const deleteHistoryEntry = async (id) => {
    if (!window.confirm('Delete this send record from history? Leads are not affected.')) return;
    try {
      await axios.delete(`${API}/api/admin/reach/history/${id}`, { headers: tokenHeader() });
      toast({ title: 'History entry deleted' });
      fetchStats();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const scanInbox = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/api/admin/reach/scan-inbox`, {}, { headers: tokenHeader() });
      const d = r.data || {};
      const bits = [];
      if (d.leads_marked_replied != null) bits.push(`${d.leads_marked_replied} marked replied`);
      if (d.leads_marked_bounced != null) bits.push(`${d.leads_marked_bounced} bounced`);
      if (d.autoresponders_skipped) bits.push(`${d.autoresponders_skipped} auto-replies skipped`);
      toast({
        title: d.success ? 'Inbox scanned' : 'Scan completed with issues',
        description: bits.join(' · ') || 'No new matches found in the last 24h.',
        variant: d.success ? 'default' : 'destructive',
      });
      fetchStats();
    } catch (err) {
      toast({ title: 'Could not scan inbox', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setScanning(false); }
  };

  useEffect(() => {
    if (!sessionStorage.getItem('admin_token')) { navigate('/admin'); return; }
    fetchStats();
  }, []); // eslint-disable-line

  const Tile = ({ label, value, sub, color, testid }) => (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-5" data-testid={testid}>
      <p className="text-gray-400 text-xs uppercase tracking-wide">{label}</p>
      <p className={`text-3xl font-bold mt-1 ${color || 'text-white'}`}>{value ?? 0}</p>
      {sub != null && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );

  // Tiny inline stat used in the Recent Sends rows
  const Stat = ({ label, value, color, muted }) => (
    <span className={`inline-flex items-baseline gap-1 ${muted ? 'opacity-50' : ''}`}>
      <span className={`font-semibold ${color || 'text-gray-300'}`}>{value}</span>
      <span className="text-gray-500">{label}</span>
    </span>
  );

  const fmtScan = (iso) => {
    if (!iso) return 'never';
    try {
      const d = new Date(iso);
      const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins} min ago`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return `${hrs} hr ago`;
      return d.toLocaleString();
    } catch { return iso; }
  };

  const canScan = scanInfo?.imap_configured && scanInfo?.imap_verified;

  return (
    <ReachShell active="/admin/reach/analytics">
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Analytics</h1>
          <p className="text-gray-400 text-sm">Email performance and lead pipeline — across every Reach send.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={scanInbox}
            size="sm"
            disabled={scanning || !canScan}
            title={!canScan ? 'Configure & verify IMAP in Outreach → SMTP to enable inbox scanning' : 'Scan inbox now for new replies and bounces'}
            className="text-white"
            style={{ background: ACCENT }}
            data-testid="reach-scan-inbox-btn"
          >
            <Inbox className={`w-4 h-4 mr-1.5 ${scanning ? 'animate-pulse' : ''}`} />
            {scanning ? 'Scanning…' : 'Scan inbox now'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setLoading(true); fetchStats(); }}
            className="border-gray-700 text-gray-300 hover:text-white"
            data-testid="reach-analytics-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      {scanInfo && (
        <div className="mb-6 flex flex-wrap items-center gap-2 text-xs" data-testid="reach-scan-status">
          {canScan ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
              <CheckCircle className="w-3.5 h-3.5" />Reply detection: auto (every 15 min)
            </span>
          ) : scanInfo.imap_configured ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-orange-500/10 text-orange-300 border border-orange-500/30">
              <AlertTriangle className="w-3.5 h-3.5" />IMAP configured but not verified — click 'Test IMAP Connection' in Outreach → SMTP
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-gray-700/50 text-gray-400 border border-gray-700">
              <AlertTriangle className="w-3.5 h-3.5" />IMAP not configured — set it up in Outreach → SMTP to auto-detect replies
            </span>
          )}
          <span className="text-gray-500">Last reply scan: <span className="text-gray-300">{fmtScan(scanInfo.last_reply_scan_at)}</span></span>
          <span className="text-gray-500">· Last bounce scan: <span className="text-gray-300">{fmtScan(scanInfo.last_bounce_scan_at)}</span></span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-[#e6007e]" /></div>
      ) : (
        <>
          <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Mail className="w-4 h-4 text-[#e6007e]" />Email Performance</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <Tile label="Sent" value={stats?.emails?.sent} testid="reach-stat-sent" />
            <Tile label="Opened" value={stats?.emails?.opened} sub={`${stats?.rates?.open_rate || 0}% open rate`} color="text-emerald-400" testid="reach-stat-opened" />
            <Tile label="Bounced" value={stats?.emails?.bounced} sub={`${stats?.rates?.bounce_rate || 0}% bounce rate`} color="text-orange-400" testid="reach-stat-bounced" />
            <Tile label="Responded" value={stats?.emails?.responded} sub={`${stats?.rates?.response_rate || 0}% response rate`} color="text-purple-400" testid="reach-stat-responded" />
            <Tile label="Unsubscribed" value={stats?.emails?.unsubscribed} sub={`${stats?.rates?.unsubscribe_rate || 0}% unsub rate`} color="text-red-400" testid="reach-stat-unsubscribed" />
          </div>
          {!!stats?.emails?.queued && (
            <p className="text-xs text-gray-500 mb-8">{stats.emails.queued} email(s) still queued.</p>
          )}
          {!stats?.emails?.queued && <div className="mb-8" />}

          <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><span className="text-[#e6007e]">●</span>Lead Pipeline</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Tile label="Total Leads" value={stats?.leads?.total} testid="reach-stat-leads-total" />
            <Tile label="Contacted" value={stats?.leads?.contacted} color="text-blue-400" testid="reach-stat-leads-contacted" />
            <Tile label="Replied" value={stats?.leads?.replied} color="text-purple-400" testid="reach-stat-leads-replied" />
            <Tile label="Bounced" value={stats?.leads?.bounced} color="text-orange-400" testid="reach-stat-leads-bounced" />
            <Tile label="Unsubscribed" value={stats?.leads?.unsubscribed} color="text-red-400" testid="reach-stat-leads-unsubscribed" />
          </div>

          {/* Recent sends */}
          <div className="mt-8" data-testid="reach-history">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#e6007e]" />Recent sends
              </h2>
              <span className="text-xs text-gray-500">last {history.length || 0}</span>
            </div>
            {history.length === 0 ? (
              <div className="bg-gray-800 border border-gray-800 rounded-lg p-6 text-center text-sm text-gray-500">
                No sends yet. Open a folder and click <span className="text-gray-300">Send to all</span> to fire your first one.
              </div>
            ) : (
              <div className="bg-gray-800 border border-gray-800 rounded-lg divide-y divide-gray-800/80 overflow-hidden">
                {history.map((h) => {
                  const sent = h.sent || 0;
                  const opened = h.opened || 0;
                  const replied = h.replied || 0;
                  const bounced = h.bounced || 0;
                  const failed = h.failed || 0;
                  const queued = h.queued || 0;
                  return (
                    <div key={h.campaign_id} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 group hover:bg-gray-900/30" data-testid={`reach-history-row-${h.campaign_id}`}>
                      <span className="font-mono text-xs text-gray-500 w-20 shrink-0">
                        {h.created_at ? new Date(h.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                      {h.folder && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-gray-300">
                          <Folder className="w-3.5 h-3.5 text-[#e6007e]" />
                          <span className="text-white font-medium truncate max-w-[180px]">{h.folder}</span>
                        </span>
                      )}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ml-auto mr-2">
                        <Stat label="sent" value={sent} color="text-emerald-300" />
                        <Stat label="opened" value={opened} color="text-emerald-400" muted={!opened} />
                        <Stat label="replied" value={replied} color="text-purple-300" muted={!replied} />
                        <Stat label="bounced" value={bounced} color="text-orange-300" muted={!bounced} />
                        {failed > 0 && <Stat label="failed" value={failed} color="text-red-300" />}
                        {queued > 0 && <Stat label="queued" value={queued} color="text-yellow-300" />}
                      </div>
                      <button
                        onClick={() => deleteHistoryEntry(h.campaign_id)}
                        className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 p-1 transition-opacity"
                        data-testid={`reach-history-delete-${h.campaign_id}`}
                        title="Delete this history entry"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="text-xs text-gray-500 leading-relaxed border border-gray-800 rounded-lg p-3 bg-gray-900/50 mt-6">
            <strong className="text-gray-400">How metrics are tracked:</strong> Opens come from a 1×1 pixel injected into every HTML email.
            Bounces come from your SMTP server's rejections <em>and</em> any bounce / undeliverable messages sitting in your IMAP inbox.
            Unsubscribes happen when a recipient clicks the footer link. Responses are auto-detected by scanning your inbox every 15 minutes
            (autoresponders like "out of office" are filtered out) — or click <strong className="text-gray-300">Scan inbox now</strong> to force a check.
          </div>
        </>
      )}
    </ReachShell>
  );
};

export default ReachFoldersPage;
